import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Form, DatePicker, Select, Card, message, InputNumber, Input, Spin } from 'antd';
import { Row, Col } from 'react-bootstrap';
import { setDeliveryDetails, setDeliveryExtraWork, setOrderId } from './store/actionCreators';
import { getDays, checkZipCode, createAlaCarteOrder, stripeCheckout } from '../../data/API';
import { getMyData } from '../../utils/token';
import GoogleAutoComplete from 'react-google-autocomplete';
import moment from 'moment';
import dayjs from 'dayjs';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PAYMENT_KEY);

const { TextArea } = Input;
const apiKey = 'AIzaSyDDe0qbMj5dCBHkpxOiRIL1qrz7bbA7oVo';

const DeliveryDetails = (props) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { deliveryDetails, deliveryAddressFromRental, boxCount, packingSupplies, movingSupplies, pricing, orderId } = useSelector(state => state.alaCarte);
  const { isParentNextStepClicked, setParentNextStepClicked, isParentPayClicked, setParentPayClicked, validationSuccessRef, customerData, deliveryWindows, extraWork, tabref, isPackingOnly } = props;

  const [unavailableDates, setUnavailableDates] = useState([]);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryAddressError, setDeliveryAddressError] = useState('');
  const [zipCodeError, setZipCodeError] = useState('');
  const [timingOne, setTimingOne] = useState(null);
  const [timingTwo, setTimingTwo] = useState(null);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  useEffect(() => {
    fetchUnavailableDates();

    const address = deliveryAddressFromRental?.delivery_address ||
                   customerData?.custormerData?.delivery_address?.address ||
                   customerData?.delivery_address ||
                   customerData?.address ||
                   '';
    setDeliveryAddress(address);

    if (deliveryDetails && deliveryDetails.delivery_date) {
      form.setFieldsValue({
        delivery_date: dayjs(deliveryDetails.delivery_date),
        delivery_window_1_id: deliveryDetails.delivery_window_1_id,
        delivery_window_2_id: deliveryDetails.delivery_window_2_id,
        apt_number: deliveryDetails.apt_number,
        extra_work_id: deliveryDetails.extra_work_id,
        description: deliveryDetails.description
      });
      setTimingOne(deliveryDetails.delivery_window_1_id);
      setTimingTwo(deliveryDetails.delivery_window_2_id);
    }
  }, [customerData, deliveryAddressFromRental]);

  useEffect(() => {
    if (tabref?.current) {
      window.scrollTo({
        top: tabref.current.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  }, [tabref]);

  useEffect(() => {
    if (isParentNextStepClicked) {
      handleNext();
    }
  }, [isParentNextStepClicked]);

  // Handle payment for packing-only orders (no pickup required)
  useEffect(() => {
    if (isParentPayClicked && isPackingOnly) {
      setParentPayClicked(false);
      handlePayment();
    }
  }, [isParentPayClicked, isPackingOnly]);

  const fetchUnavailableDates = async () => {
    try {
      const response = await getDays();
      if (response?.data) {
        setUnavailableDates(response.data.map(d => d.date));
      }
    } catch (error) {
      console.error('Error fetching unavailable dates:', error);
    }
  };

  const changeDate = (date) => {
    if (date) {
      // Clear delivery windows when date changes
      form.setFieldsValue({ delivery_window_1_id: null, delivery_window_2_id: null });
      setTimingOne(null);
      setTimingTwo(null);
    }
  };

  const handleTimingOneChange = (evt) => {
    setTimingOne(evt);

    // If window 1 equals window 2, clear window 2 (matching original implementation)
    if (evt == timingTwo) {
      setTimingTwo(null);
      form.setFieldsValue({ delivery_window_2_id: null });
    }
  };

  const handleTimingTwoChange = (evt) => {
    setTimingTwo(evt);
  };

  const setAddress = async (place) => {
    setDeliveryAddress(place?.formatted_address);
    setDeliveryAddressError('');

    let postal, country, city, state;
    place.address_components.forEach((address_element) => {
      if (address_element.types.includes('postal_code')) postal = address_element.long_name;
      if (address_element.types.includes('country')) country = address_element.short_name;
      if (address_element.types.includes('locality')) city = address_element.long_name;
      if (address_element.types.includes('administrative_area_level_1'))
        state = address_element.long_name;
    });

    let zipCodeRes = await checkZipCode({ deliveryzipcode: postal ? postal : '781006' });

    if (zipCodeRes?.data?.success === true) {
      setZipCodeError(null);
    } else {
      setZipCodeError(zipCodeRes?.data?.message_2);
    }
  };

  const onAddresschange = (e) => {
    if (deliveryAddress && e.target.value == 0) {
      setDeliveryAddress('');
      setDeliveryAddressError('Please Select Delivery Address');
    }
  };

  const handleNext = async () => {
    try {
      if (!deliveryAddress) {
        setDeliveryAddressError('Please select delivery address');
        message.error('Please complete all delivery details');
        if (validationSuccessRef) validationSuccessRef.current = false;
        setParentNextStepClicked(false);
        return;
      }

      if (zipCodeError) {
        message.error('Services not available in this zip code');
        if (validationSuccessRef) validationSuccessRef.current = false;
        setParentNextStepClicked(false);
        return;
      }

      const values = await form.validateFields();

      const deliveryDate = moment(values.delivery_date.$d).format('YYYY-MM-DD');

      // Save delivery details to Redux
      dispatch(setDeliveryDetails({
        delivery_date: deliveryDate,
        delivery_window_1_id: values.delivery_window_1_id,
        delivery_window_2_id: values.delivery_window_2_id,
        delivery_address: deliveryAddress,
        apt_number: values.apt_number,
        extra_work_id: values.extra_work_id,
        description: values.description
      }));

      // Validation succeeded - signal parent to proceed
      if (validationSuccessRef) validationSuccessRef.current = true;
      setParentNextStepClicked(false);
    } catch (error) {
      message.error('Please complete all delivery details');
      console.error('Validation failed:', error);
      if (validationSuccessRef) validationSuccessRef.current = false;
      setParentNextStepClicked(false);
    }
  };

  // Payment handling for packing-only orders (no pickup required)
  const handlePayment = async () => {
    try {
      await form.validateFields();
      setIsFormSubmitted(true);

      const hasItems = boxCount > 0 || packingSupplies.length > 0 || movingSupplies.length > 0;
      if (!hasItems) {
        message.error('Please select at least one item (boxes, packing supplies, or moving supplies) to proceed with checkout');
        setIsFormSubmitted(false);
        return;
      }

      // Check if delivery address is valid
      if (!deliveryAddress) {
        message.error('Please enter delivery address');
        setIsFormSubmitted(false);
        return;
      }

      if (zipCodeError) {
        message.error('Services not available in this zip code');
        setIsFormSubmitted(false);
        return;
      }

      // Check if timing windows are selected
      if (!timingOne || !timingTwo) {
        message.error('Please select both delivery time windows');
        setIsFormSubmitted(false);
        return;
      }

      // Prepare order data
      const userData = getMyData();
      const values = await form.validateFields();

      // Get delivery date from form
      const deliveryDate = moment(values.delivery_date.$d).format('YYYY-MM-DD');

      // Save delivery details to Redux first
      dispatch(setDeliveryDetails({
        delivery_date: deliveryDate,
        delivery_window_1_id: values.delivery_window_1_id,
        delivery_window_2_id: values.delivery_window_2_id,
        delivery_address: deliveryAddress,
        apt_number: values.apt_number,
        extra_work_id: values.extra_work_id,
        description: values.description
      }));

      const orderData = {
        customer_id: customerData?.id,
        box_count: boxCount,
        packing_supplies: packingSupplies.map(item => ({
          product_id: item.id,
          quantity: item.quantity
        })),
        moving_supplies: movingSupplies.map(item => ({
          product_id: item.id,
          quantity: item.quantity
        })),
        delivery_details: {
          delivery_date: deliveryDate,
          delivery_window_1_id: values.delivery_window_1_id,
          delivery_window_2_id: values.delivery_window_2_id,
          delivery_address: deliveryAddress,
          extra_work_id: values.extra_work_id || null,
          apt_number: values.apt_number || null,
          description: values.description || ''
        },
        // No pickup_details for packing-only orders
        pickup_details: null,
        total_amount: pricing.total,
        transportation_fee: pricing.transportationFee,
        tax: pricing.tax
      };

      // Check if order already exists (to prevent duplicate orders)
      let currentOrderId = orderId;

      if (!currentOrderId) {
        // Create order only if it doesn't exist
        const orderResponse = await createAlaCarteOrder(orderData);
        console.log('Order response:', orderResponse);

        // Check for order_id in nested data structure
        currentOrderId = orderResponse?.data?.data?.order_id || orderResponse?.data?.order_id;

        if (!currentOrderId) {
          message.error('Failed to create order');
          setIsFormSubmitted(false);
          return;
        }

        // Save order ID to Redux to prevent duplicate creation
        dispatch(setOrderId(currentOrderId));
        console.log('Created order ID:', currentOrderId);
      } else {
        console.log('Using existing order ID:', currentOrderId);
      }

      // Create Stripe checkout session
      const userEmail = userData?.email;
      const stripeSession = await stripeCheckout({
        payment: { billing_details: { email: userEmail } },
        session: currentOrderId,
        order_type: 'packages',
        amount: pricing.total,
        discount: 0,
        driver_tip: 0
      });

      console.log('Stripe session response:', stripeSession);

      if (stripeSession?.data?.sessionId) {
        console.log('Redirecting to Stripe checkout with sessionId:', stripeSession.data.sessionId);
        const stripe = await stripePromise;
        const result = await stripe.redirectToCheckout({
          sessionId: stripeSession.data.sessionId
        });

        if (result.error) {
          console.error('Stripe redirect error:', result.error);
          message.error('Payment redirect failed. Please try again.');
          setIsFormSubmitted(false);
        }
      } else {
        console.error('No sessionId in response:', stripeSession);
        message.error('Failed to create checkout session');
        setIsFormSubmitted(false);
      }
    } catch (error) {
      console.error('Payment error:', error);

      if (error.response?.data?.errors) {
        const errorMessage = Object.values(error.response.data.errors).flat().join(', ');
        message.error(errorMessage);
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Please complete all required delivery details before checkout');
      }

      setIsFormSubmitted(false);
    }
  };

  const disableDate = (current) => {
    if (!current) return false;

    // Disable past dates
    if (current && current < dayjs().startOf('day')) {
      return true;
    }

    // Disable unavailable dates
    const dateStr = current.format('YYYY-MM-DD');
    return unavailableDates.includes(dateStr);
  };

  return (
    <div>
      <div className="steps bg-white-100" id="steps">
        <h2 className="shopping-cart-header text-white text-center py-2 fs-20 steps-title">
          Delivery Details
        </h2>
        <Card bordered={false} className="w-100 px-3 pt-3 pb-4">
          <Spin spinning={isFormSubmitted} tip="processing...">
            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              style={{ position: 'relative', zIndex: 0 }}
              initialValues={{
                delivery_date: deliveryDetails?.delivery_date ? dayjs(deliveryDetails.delivery_date) : null,
                delivery_window_1_id: deliveryDetails?.delivery_window_1_id || null,
                delivery_window_2_id: deliveryDetails?.delivery_window_2_id || null,
                apt_number: deliveryDetails?.apt_number || null,
                extra_work_id: deliveryDetails?.extra_work_id || null,
                description: deliveryDetails?.description || ''
              }}
            >
              <Row>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="delivery_date"
                    label="Delivery Date"
                    rules={[{ required: true, message: 'Please choose a delivery date' }]}
                  >
                    <DatePicker
                      className="w-100"
                      size="large"
                      format="MM-DD-YYYY"
                      disabledDate={disableDate}
                      onChange={changeDate}
                    />
                  </Form.Item>
                </Col>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="delivery_window_1_id"
                    label="Delivery Window 1"
                    rules={[{ required: true, message: 'Please choose a delivery window 1' }]}
                  >
                    <Select
                      size="large"
                      placeholder="Select a time slot"
                      value={timingOne}
                      onChange={handleTimingOneChange}
                    >
                      {deliveryWindows?.sort((a, b) => {
                        // Handle "Anytime" slots - put them at the end
                        if (a.start_time.toLowerCase().includes('anytime')) return 1;
                        if (b.start_time.toLowerCase().includes('anytime')) return -1;

                        // Convert 12-hour format to 24-hour for comparison
                        const convertTo24Hour = (timeStr) => {
                          const [time, period] = timeStr.split(' ');
                          let [hours, minutes] = time.split(':').map(Number);
                          if (period?.toLowerCase() === 'pm' && hours !== 12) hours += 12;
                          if (period?.toLowerCase() === 'am' && hours === 12) hours = 0;
                          return hours * 60 + (minutes || 0);
                        };

                        return convertTo24Hour(a.start_time) - convertTo24Hour(b.start_time);
                      }).map((options, index) => {
                        return (
                          <Select.Option
                            key={`timing_one_${index}`}
                            hidden={options.id == timingTwo}
                            value={options.id}
                          >
                            {`${options.start_time} - ${options.end_time}`}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="delivery_window_2_id"
                    label="Delivery Window 2"
                    rules={[{ required: true, message: 'Please choose a delivery window 2' }]}
                  >
                    <Select
                      size="large"
                      placeholder="Select a time slot"
                      onChange={handleTimingTwoChange}
                      value={timingTwo}
                      disabled={!timingOne}
                    >
                      {deliveryWindows?.sort((a, b) => {
                        // Handle "Anytime" slots - put them at the end
                        if (a.start_time.toLowerCase().includes('anytime')) return 1;
                        if (b.start_time.toLowerCase().includes('anytime')) return -1;

                        // Convert 12-hour format to 24-hour for comparison
                        const convertTo24Hour = (timeStr) => {
                          const [time, period] = timeStr.split(' ');
                          let [hours, minutes] = time.split(':').map(Number);
                          if (period?.toLowerCase() === 'pm' && hours !== 12) hours += 12;
                          if (period?.toLowerCase() === 'am' && hours === 12) hours = 0;
                          return hours * 60 + (minutes || 0);
                        };

                        return convertTo24Hour(a.start_time) - convertTo24Hour(b.start_time);
                      }).map((options, index) => {
                        return (
                          <Select.Option
                            hidden={options.id == timingOne}
                            key={`timing_two_${index}`}
                            value={options.id}
                          >
                            {`${options.start_time} - ${options.end_time}`}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    className="mandatory-field"
                    name="delivery_address"
                    label="Delivery Address"
                  >
                    <GoogleAutoComplete
                      className={
                        deliveryAddressError && !deliveryAddress
                          ? 'form-control delivery-address-field-error '
                          : 'form-control'
                      }
                      apiKey={apiKey}
                      onPlaceSelected={(place) => {
                        setAddress(place);
                      }}
                      onChange={(e) => {
                        onAddresschange(e);
                      }}
                      options={{
                        types: ['address'],
                        componentRestrictions: { country: 'us' }
                      }}
                      defaultValue={deliveryAddress}
                    />
                    <p className="mb-0 ant-form-item-explain-error">
                      {zipCodeError ? zipCodeError : null}
                    </p>
                    <p className="mb-0 ant-form-item-explain-error">
                      {deliveryAddressError && !deliveryAddress ? deliveryAddressError : ''}
                    </p>
                    <p className="mb-0 text-success">
                      {!zipCodeError && deliveryAddress ? 'Services available in this area' : null}
                    </p>
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item name="apt_number" label="Apt." rules={[{ required: false }]}>
                    <InputNumber
                      className="w-100"
                      placeholder="Apt."
                      size="large"
                      controls={false}
                    />
                  </Form.Item>
                </Col>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="extra_work_id"
                    label="Do you have stairs or an elevator?"
                    rules={[{ required: true, message: 'Please select an option from below' }]}
                  >
                    <Select
                      size="large"
                      onChange={(value) => {
                        const selectedExtraWork = extraWork?.find(work => work.id === value);
                        if (selectedExtraWork) {
                          dispatch(setDeliveryExtraWork({
                            id: selectedExtraWork.id,
                            title: selectedExtraWork.title,
                            price: selectedExtraWork.price
                          }));
                        }
                      }}
                    >
                      {extraWork?.map((obj, index) => {
                        return (
                          <Select.Option
                            key={`extarwork_${index}`}
                            value={obj?.id}
                          >
                            {`${obj.title} ($${obj.price})`}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col className="px-3 py-0" xxl={6} xl={6} lg={6} md={6} sm={12} xs={12}>
                  <Form.Item
                    name="description"
                    label="Is there anything else we should know?"
                    rules={[{ required: false }]}
                  >
                    <TextArea
                      autoSize={{
                        minRows: 3,
                        maxRows: 5
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </Card>
      </div>
    </div>
  );
};

DeliveryDetails.propTypes = {
  isParentNextStepClicked: PropTypes.bool,
  setParentNextStepClicked: PropTypes.func,
  isParentPayClicked: PropTypes.bool,
  setParentPayClicked: PropTypes.func,
  validationSuccessRef: PropTypes.object,
  customerData: PropTypes.object,
  deliveryWindows: PropTypes.array,
  extraWork: PropTypes.array,
  tabref: PropTypes.object,
  isPackingOnly: PropTypes.bool,
};

export default DeliveryDetails;
